import React, { useState, useEffect, useRef } from 'react';
import formStyles from '../../styles/SmartSendForm.module.css';
import { isDevelopment } from '../../env/config';

/**
 * Field configuration for missing recipient information fields
 * Defines labels, types, placeholders, and validation for each field
 */
const fieldConfig = {
  firstName: {
    label: 'First Name',
    type: 'text',
    placeholder: 'John',
    validation: (value) => {
      if (!value.trim()) return 'First name is required';
      return null;
    },
  },
  lastName: {
    label: 'Last Name',
    type: 'text',
    placeholder: 'Doe',
    validation: (value) => {
      if (!value.trim()) return 'Last name is required';
      return null;
    },
  },
  email: {
    label: 'Email',
    type: 'email',
    placeholder: 'john@example.com',
    validation: (value) => {
      if (!value.trim()) return 'Email is required';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        return 'Please enter a valid email address';
      }
      return null;
    },
  },
  phone: {
    label: 'Phone Number',
    type: 'tel',
    placeholder: '(555) 123-4567',
    validation: (value) => {
      if (!value.trim()) return 'Phone number is required';
      // Basic phone validation - at least 10 digits
      const digitsOnly = value.replace(/\D/g, '');
      if (digitsOnly.length < 10) {
        return 'Please enter a valid phone number';
      }
      return null;
    },
  },
};

/**
 * MissingInfoModal - Modal for collecting missing recipient information
 * Opens when backend returns RECIPIENT_INFO_REQUIRED
 * 
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {Array<string>} missingFields - Array of missing field names (e.g., ['firstName', 'lastName', 'email', 'phone'])
 * @param {string} message - Message from backend to display to user
 * @param {Function} onSubmit - Handler for submitting collected information
 * @param {boolean} isSubmitting - Loading state during submission
 * @param {Object} fieldErrors - Field-specific error messages from backend
 * @param {Function} onClose - Handler for closing the modal
 * @param {Object} initialFormData - Initial form data to populate (for persistence)
 * @param {Function} onFormDataChange - Callback when form data changes (for persistence)
 */
const MissingInfoModal = ({
  isOpen,
  missingFields = [],
  message = "Looks like we're missing some details — please update your information.",
  onSubmit,
  isSubmitting = false,
  fieldErrors = {},
  onClose,
  initialFormData = {},
  onFormDataChange,
}) => {
  // State for each possible field - initialize from initialFormData for persistence
  const [formData, setFormData] = useState({
    firstName: initialFormData.firstName || '',
    lastName: initialFormData.lastName || '',
    email: initialFormData.email || '',
    phone: initialFormData.phone || '',
  });

  const [errors, setErrors] = useState({});
  const modalContentRef = useRef(null);
  const firstInputRef = useRef(null);
  const previousActiveElementRef = useRef(null);
  const wasOpenRef = useRef(false);
  const hasFocusedFirstInputRef = useRef(false);
  const onCloseRef = useRef(onClose);
  const isSubmittingRef = useRef(isSubmitting);

  // Keep refs in sync with props to avoid stale closures
  useEffect(() => {
    onCloseRef.current = onClose;
    isSubmittingRef.current = isSubmitting;
  }, [onClose, isSubmitting]);

  // Update form data only when modal opens (not on every initialFormData change)
  // This prevents overwriting user input if initialFormData changes while modal is open
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      // Modal just opened - initialize form data from persisted data
      setFormData({
        firstName: initialFormData.firstName || '',
        lastName: initialFormData.lastName || '',
        email: initialFormData.email || '',
        phone: initialFormData.phone || '',
      });
      // Clear any stale errors from previous modal session
      setErrors({});
      wasOpenRef.current = true;
    } else if (!isOpen) {
      // Modal closed - reset flag for next open
      wasOpenRef.current = false;
    }
  }, [isOpen, initialFormData]);

  // Notify parent of form data changes for persistence
  useEffect(() => {
    if (onFormDataChange) {
      onFormDataChange(formData);
    }
  }, [formData, onFormDataChange]);

  // Focus management: trap focus within modal and handle Escape key
  useEffect(() => {
    if (!isOpen) {
      // Reset focus flag when modal closes
      hasFocusedFirstInputRef.current = false;
      return;
    }

    // Store the element that had focus before modal opened (only on first open)
    if (!hasFocusedFirstInputRef.current) {
      previousActiveElementRef.current = document.activeElement;
    }

    // Focus the first input when modal opens (only once, not on every render)
    if (!hasFocusedFirstInputRef.current && firstInputRef.current) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (firstInputRef.current && !hasFocusedFirstInputRef.current) {
            firstInputRef.current.focus();
            hasFocusedFirstInputRef.current = true;
          }
        }, 0);
      });
    }

    // Get all focusable elements within the modal
    const getFocusableElements = () => {
      if (!modalContentRef.current) return [];
      const focusableSelectors = [
        'button:not([disabled])',
        '[href]',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(', ');
      return Array.from(
        modalContentRef.current.querySelectorAll(focusableSelectors)
      );
    };

    // Handle Escape key to close modal
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !isSubmittingRef.current) {
        onCloseRef.current();
      }
    };

    // Trap focus within modal (only on Tab key, not on mouse clicks)
    const handleTab = (e) => {
      // Only handle Tab key presses, ignore all other events
      if (e.key !== 'Tab') return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;

      // Only trap if focus is currently within the modal
      // This check ensures we don't interfere with mouse clicks
      if (
        !modalContentRef.current ||
        !modalContentRef.current.contains(document.activeElement)
      ) {
        // If focus escaped (via Tab), bring it back to first element
        e.preventDefault();
        focusableElements[0].focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      // If Shift+Tab on first element, move to last element
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
        return;
      }

      // If Tab on last element, move to first element
      if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
        return;
      }
    };

    // Add event listeners
    document.addEventListener('keydown', handleEscape);
    document.addEventListener('keydown', handleTab);

    // Cleanup: remove event listeners
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('keydown', handleTab);
    };
  }, [isOpen]); // Only depend on isOpen to prevent unnecessary re-runs

  // Restore focus to previous element only when modal closes
  const previousIsOpenRef = useRef(isOpen);
  useEffect(() => {
    if (previousIsOpenRef.current && !isOpen) {
      // Modal just closed - restore focus to previous element
      if (previousActiveElementRef.current) {
        const previousElement = previousActiveElementRef.current;
        if (
          document.body.contains(previousElement) &&
          typeof previousElement.focus === 'function'
        ) {
          setTimeout(() => {
            previousElement.focus();
          }, 0);
        }
      }
    }
    previousIsOpenRef.current = isOpen;
  }, [isOpen]);

  // Log missing fields to help debug which fields are being requested (development only)
  useEffect(() => {
    if (isOpen && isDevelopment()) {
      console.log('🔍 [MissingInfoModal] Rendering with missing fields:', {
        missingFields,
        fieldCount: missingFields.length,
        fields: missingFields.map((field) => ({
          field,
          hasConfig: !!fieldConfig[field],
        })),
      });
    }
  }, [isOpen, missingFields]);

  /**
   * Handle input change
   */
  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  // Update local errors when fieldErrors prop changes (from backend)
  useEffect(() => {
    if (Object.keys(fieldErrors).length > 0) {
      // Merge fieldErrors into local errors state for display
      setErrors((prev) => {
        const merged = { ...prev };
        Object.keys(fieldErrors).forEach((field) => {
          if (fieldErrors[field]) {
            merged[field] = fieldErrors[field];
          }
        });
        return merged;
      });
    }
  }, [fieldErrors]);

  /**
   * Validate all missing fields
   */
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    missingFields.forEach((field) => {
      const config = fieldConfig[field];
      if (config) {
        const error = config.validation(formData[field] || '');
        if (error) {
          newErrors[field] = error;
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = () => {
    if (validateForm()) {
      // Only submit fields that were requested
      const dataToSubmit = {};
      missingFields.forEach((field) => {
        dataToSubmit[field] = formData[field];
      });
      onSubmit(dataToSubmit);
    }
  };

  // Check if form is complete
  const isFormComplete = missingFields.every((field) =>
    formData[field]?.trim()
  );

  if (!isOpen) return null;

  return (
    <div
      className={formStyles.modalOverlay}
      onClick={!isSubmitting ? onClose : undefined}
      aria-busy={isSubmitting}
    >
      <div
        ref={modalContentRef}
        className={formStyles.modalContent}
        role="dialog"
        aria-modal="true"
        aria-labelledby="missing-info-modal-title"
        aria-describedby="missing-info-modal-description"
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          className={formStyles.modalTitle}
          id="missing-info-modal-title"
        >
          Additional Information Required
        </h3>
        <p
          className={`${formStyles.modalMessage} ${
            Object.keys(fieldErrors).length > 0
              ? formStyles.modalMessageError
              : ''
          }`}
          id="missing-info-modal-description"
        >
          {message}
        </p>

        <div
          className={formStyles.fieldContainer}
          role="group"
          aria-labelledby="missing-info-modal-title"
          aria-describedby="missing-info-modal-description"
        >
          {missingFields.map((field, index) => {
            const config = fieldConfig[field];
            if (!config) {
              if (isDevelopment()) {
                console.warn(
                  `🔍 [MissingInfoModal] Unknown field requested: "${field}". Available fields:`,
                  Object.keys(fieldConfig)
                );
              }
              return null;
            }

            const isLastField = index === missingFields.length - 1;
            
            return (
              <div key={field} className={isLastField ? formStyles.lastField : undefined}>
                <label className={formStyles.label} htmlFor={field}>
                  {config.label}
                </label>
                <input
                  ref={index === 0 ? firstInputRef : null}
                  id={field}
                  type={config.type}
                  placeholder={config.placeholder}
                  className={formStyles.input}
                  value={formData[field]}
                  onChange={(e) => handleChange(field, e.target.value)}
                  disabled={isSubmitting}
                  aria-label={config.label}
                  aria-required="true"
                  aria-invalid={!!errors[field]}
                  aria-describedby={errors[field] ? `${field}-error` : undefined}
                />
                {errors[field] && (
                  <p
                    className={formStyles.errorText}
                    id={`${field}-error`}
                    role="alert"
                  >
                    {errors[field]}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <div className={formStyles.modalActions}>
          <button
            type="button"
            className={`${formStyles.modalButton} ${formStyles.modalButtonSecondary}`}
            onClick={onClose}
            disabled={isSubmitting}
          >
            Close
          </button>
          <button
            type="button"
            className={`${formStyles.modalButton} ${formStyles.modalButtonPrimary}`}
            onClick={handleSubmit}
            disabled={!isFormComplete || isSubmitting}
            aria-label={isSubmitting ? 'Submitting information' : 'Submit additional information'}
            aria-busy={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MissingInfoModal;

